import { useCallback, useEffect, useMemo, useState } from 'react';
import { kuunda } from '@/lib/kuunda';
import { AdminEmpty, AdminKpiCard, AdminLoadingGrid, AdminPageHeader } from '@/features/admin/ui';
import { ListSkeleton } from '@/features/ui/skeleton';
import { useToast } from '@/features/ui/toast/useToast';
import '@/features/admin/admin.css';
import '@/features/analytics/analytics.css';

type RangeKey = '1d' | '7d' | '30d' | '90d';

type AudienceKpis = {
  visitors: number;
  sessions: number;
  pageviews: number;
  conversions: number;
  bounce_rate: number;
  avg_pages_per_session: number;
  avg_session_duration_sec: number;
  new_visitors: number;
  returning_visitors: number;
  authenticated_sessions: number;
};

type NamedCount = { path?: string; source?: string; journey?: string; device_type?: string; views?: number; visitors?: number; sessions?: number; hits?: number };
type CampaignRow = {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  hits: number;
  visitors: number;
};
type DailyRow = { day: string; pageviews: number; visitors: number; sessions: number; conversions: number };
type Funnel = {
  landing: number;
  presentation: number;
  marketplace: number;
  contact: number;
  inscription: number;
  connexion: number;
  app: number;
  conversion_signup: number;
  conversion_contact: number;
};
type RecentRow = {
  created_at: string;
  event_type: string;
  path: string;
  visitor_id: string;
  device_type: string | null;
  referrer: string | null;
  utm_source: string | null;
  utm_campaign: string | null;
  duration_ms: number | null;
  meta: Record<string, unknown> | null;
};

type AudienceStats = {
  from: string;
  to: string;
  kpis: AudienceKpis;
  daily: DailyRow[];
  top_pages: NamedCount[];
  entry_pages: NamedCount[];
  exit_pages: NamedCount[];
  referrers: NamedCount[];
  campaigns: CampaignRow[];
  devices: NamedCount[];
  funnel: Funnel;
  journeys: NamedCount[];
  recent: RecentRow[];
};

const RANGES: { key: RangeKey; label: string; days: number }[] = [
  { key: '1d', label: '24 h', days: 1 },
  { key: '7d', label: '7 j', days: 7 },
  { key: '30d', label: '30 j', days: 30 },
  { key: '90d', label: '90 j', days: 90 },
];

const FUNNEL_STEPS: { key: keyof Funnel; label: string }[] = [
  { key: 'landing', label: 'Accueil' },
  { key: 'presentation', label: 'Présentation' },
  { key: 'marketplace', label: 'Marketplace' },
  { key: 'contact', label: 'Contact' },
  { key: 'inscription', label: 'Inscription' },
  { key: 'connexion', label: 'Connexion' },
  { key: 'app', label: 'Espace app' },
];

function emptyKpis(): AudienceKpis {
  return {
    visitors: 0,
    sessions: 0,
    pageviews: 0,
    conversions: 0,
    bounce_rate: 0,
    avg_pages_per_session: 0,
    avg_session_duration_sec: 0,
    new_visitors: 0,
    returning_visitors: 0,
    authenticated_sessions: 0,
  };
}

function formatDuration(sec: number): string {
  if (!sec || sec < 1) return '0 s';
  if (sec < 60) return `${Math.round(sec)} s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m} min ${s.toString().padStart(2, '0')} s`;
}

function BarList({
  rows,
  labelKey,
  valueKey,
}: {
  rows: NamedCount[];
  labelKey: 'path' | 'source' | 'journey' | 'device_type';
  valueKey: 'views' | 'sessions' | 'hits' | 'visitors';
}) {
  const max = Math.max(1, ...rows.map((r) => Number(r[valueKey] ?? 0)));
  if (rows.length === 0) {
    return <p className="muted" style={{ margin: 0, fontSize: '0.88rem' }}>Aucune donnée sur la période.</p>;
  }
  return (
    <div className="audience-bars">
      {rows.map((row, i) => {
        const label = String(row[labelKey] ?? '—');
        const value = Number(row[valueKey] ?? 0);
        return (
          <div key={`${label}-${i}`} className="audience-bar-row" title={label}>
            <span className="audience-bar-label">{label}</span>
            <div className="audience-bar-track">
              <div className="audience-bar-fill" style={{ width: `${(value / max) * 100}%` }} />
            </div>
            <span className="audience-bar-value">{value}</span>
          </div>
        );
      })}
    </div>
  );
}

export function AdminAudiencePage() {
  const toast = useToast();
  const [range, setRange] = useState<RangeKey>('7d');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<AudienceStats | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const days = RANGES.find((r) => r.key === range)?.days ?? 7;
    const to = new Date();
    const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
    const { data, error } = await kuunda.rpc('get_audience_stats', {
      p_from: from.toISOString(),
      p_to: to.toISOString(),
    });
    if (error) {
      toast.error(error.message);
      setStats(null);
    } else {
      const raw = (data ?? {}) as AudienceStats;
      setStats({
        ...raw,
        kpis: { ...emptyKpis(), ...(raw.kpis ?? {}) },
        daily: raw.daily ?? [],
        top_pages: raw.top_pages ?? [],
        entry_pages: raw.entry_pages ?? [],
        exit_pages: raw.exit_pages ?? [],
        referrers: raw.referrers ?? [],
        campaigns: raw.campaigns ?? [],
        devices: raw.devices ?? [],
        funnel: raw.funnel ?? {
          landing: 0,
          presentation: 0,
          marketplace: 0,
          contact: 0,
          inscription: 0,
          connexion: 0,
          app: 0,
          conversion_signup: 0,
          conversion_contact: 0,
        },
        journeys: raw.journeys ?? [],
        recent: raw.recent ?? [],
      });
    }
    setLoading(false);
  }, [range, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const kpis = stats?.kpis ?? emptyKpis();
  const funnelMax = useMemo(
    () => Math.max(1, ...FUNNEL_STEPS.map((s) => Number(stats?.funnel?.[s.key] ?? 0))),
    [stats],
  );
  const dailyMax = useMemo(
    () => Math.max(1, ...(stats?.daily ?? []).map((d) => d.pageviews || d.visitors || 0)),
    [stats],
  );

  return (
    <div className="admin-page">
      <AdminPageHeader
        title="Audience"
        description="Cookies visiteurs, sessions, pages vues et parcours — pour comprendre comment les utilisateurs découvrent et utilisent ISBD."
        actions={
          <div className="audience-toolbar">
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                className={`audience-range-btn${range === r.key ? ' is-active' : ''}`}
                onClick={() => setRange(r.key)}
              >
                {r.label}
              </button>
            ))}
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => void load()}>
              Actualiser
            </button>
          </div>
        }
      />

      {loading ? <AdminLoadingGrid count={8} /> : null}

      {!loading ? (
        <div className="admin-kpi-grid">
          <AdminKpiCard to="/admin/audience" label="Visiteurs uniques" value={kpis.visitors} icon="users" hint={`${kpis.new_visitors} nouveaux · ${kpis.returning_visitors} revenants`} />
          <AdminKpiCard to="/admin/audience" label="Sessions" value={kpis.sessions} icon="chart" hint={`${kpis.authenticated_sessions} avec compte`} />
          <AdminKpiCard to="/admin/audience" label="Pages vues" value={kpis.pageviews} icon="globe" hint={`${kpis.avg_pages_per_session} pages / session`} />
          <AdminKpiCard to="/admin/audience" label="Taux de rebond" value={`${kpis.bounce_rate} %`} icon="inbox" tone={kpis.bounce_rate > 60 ? 'warn' : 'default'} hint={`Durée moy. ${formatDuration(kpis.avg_session_duration_sec)}`} />
          <AdminKpiCard to="/admin/audience" label="Conversions" value={kpis.conversions} icon="trophy" tone="ok" hint="Inscription, contact…" />
        </div>
      ) : null}

      <div className="grid-2" style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
        <section className="admin-panel">
          <h3 className="audience-section-title">Évolution quotidienne</h3>
          {loading ? (
            <ListSkeleton rows={4} />
          ) : !stats?.daily.length ? (
            <AdminEmpty icon="chart" title="Pas encore de trafic" hint="Acceptez les cookies sur le site public pour commencer la mesure." />
          ) : (
            <div className="audience-daily" aria-label="Pages vues par jour">
              {stats.daily.map((d) => {
                const h = Math.max(4, ((d.pageviews || 0) / dailyMax) * 120);
                const label = new Date(d.day).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
                return (
                  <div key={d.day} className="audience-daily-col" title={`${label} — ${d.pageviews} vues · ${d.visitors} visiteurs`}>
                    <div className="audience-daily-bar" style={{ height: h }} />
                    <span className="audience-daily-label">{label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="admin-panel">
          <h3 className="audience-section-title">Entonnoir de découverte</h3>
          {loading ? (
            <ListSkeleton rows={5} />
          ) : (
            <>
              <div className="audience-funnel">
                {FUNNEL_STEPS.map((step) => {
                  const value = Number(stats?.funnel?.[step.key] ?? 0);
                  return (
                    <div key={step.key} className="audience-funnel-step">
                      <span>{step.label}</span>
                      <div className="audience-bar-track">
                        <div className="audience-bar-fill" style={{ width: `${(value / funnelMax) * 100}%` }} />
                      </div>
                      <strong>{value}</strong>
                    </div>
                  );
                })}
              </div>
              <p className="muted" style={{ margin: '0.85rem 0 0', fontSize: '0.84rem' }}>
                Conversions marquées : {stats?.funnel.conversion_signup ?? 0} inscriptions ·{' '}
                {stats?.funnel.conversion_contact ?? 0} contacts
              </p>
            </>
          )}
        </section>
      </div>

      <div className="grid-2" style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
        <section className="admin-panel">
          <h3 className="audience-section-title">Pages les plus vues</h3>
          {loading ? <ListSkeleton rows={5} /> : <BarList rows={stats?.top_pages ?? []} labelKey="path" valueKey="views" />}
        </section>
        <section className="admin-panel">
          <h3 className="audience-section-title">Pages d’entrée</h3>
          {loading ? <ListSkeleton rows={5} /> : <BarList rows={stats?.entry_pages ?? []} labelKey="path" valueKey="sessions" />}
        </section>
        <section className="admin-panel">
          <h3 className="audience-section-title">Pages de sortie</h3>
          {loading ? <ListSkeleton rows={5} /> : <BarList rows={stats?.exit_pages ?? []} labelKey="path" valueKey="sessions" />}
        </section>
        <section className="admin-panel">
          <h3 className="audience-section-title">Sources / référents</h3>
          {loading ? <ListSkeleton rows={5} /> : <BarList rows={stats?.referrers ?? []} labelKey="source" valueKey="hits" />}
        </section>
      </div>

      <div className="grid-2" style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
        <section className="admin-panel">
          <h3 className="audience-section-title">Appareils</h3>
          {loading ? <ListSkeleton rows={3} /> : <BarList rows={stats?.devices ?? []} labelKey="device_type" valueKey="sessions" />}
        </section>
        <section className="admin-panel">
          <h3 className="audience-section-title">Campagnes UTM</h3>
          {loading ? (
            <ListSkeleton rows={4} />
          ) : !(stats?.campaigns?.length) ? (
            <p className="muted" style={{ margin: 0, fontSize: '0.88rem' }}>
              Aucune campagne UTM détectée (ajoutez ?utm_source=… aux liens).
            </p>
          ) : (
            <div className="admin-table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Medium</th>
                    <th>Campagne</th>
                    <th>Hits</th>
                    <th>Visiteurs</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.campaigns.map((c, i) => (
                    <tr key={`${c.utm_campaign}-${i}`}>
                      <td>{c.utm_source}</td>
                      <td>{c.utm_medium}</td>
                      <td>{c.utm_campaign}</td>
                      <td>{c.hits}</td>
                      <td>{c.visitors}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <section className="admin-panel">
        <h3 className="audience-section-title">Parcours fréquents (jusqu’à 5 étapes)</h3>
        {loading ? (
          <ListSkeleton rows={6} />
        ) : !(stats?.journeys?.length) ? (
          <AdminEmpty icon="chart" title="Pas encore de parcours" hint="Les enchaînements de pages apparaîtront ici." />
        ) : (
          <BarList rows={stats.journeys} labelKey="journey" valueKey="sessions" />
        )}
      </section>

      <section className="admin-panel">
        <h3 className="audience-section-title">Activité récente</h3>
        {loading ? (
          <ListSkeleton rows={6} />
        ) : !(stats?.recent?.length) ? (
          <AdminEmpty icon="inbox" title="Aucun événement" />
        ) : (
          <div className="admin-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Quand</th>
                  <th>Type</th>
                  <th>Page</th>
                  <th>Appareil</th>
                  <th>UTM</th>
                  <th>Durée</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent.map((ev, i) => (
                  <tr key={`${ev.created_at}-${i}`}>
                    <td>{new Date(ev.created_at).toLocaleString('fr-FR')}</td>
                    <td>
                      {ev.event_type}
                      {ev.meta && typeof ev.meta === 'object' && 'kind' in ev.meta
                        ? ` · ${String((ev.meta as { kind?: string }).kind ?? '')}`
                        : ''}
                    </td>
                    <td>{ev.path}</td>
                    <td>{ev.device_type || '—'}</td>
                    <td>{ev.utm_source || ev.utm_campaign || '—'}</td>
                    <td>
                      {ev.duration_ms != null ? formatDuration(ev.duration_ms / 1000) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
